import { FC, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
	ButtonGroup,
	DialogTitle,
	PunchupDialogue,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { previewEventSeatsChart, syncEventSeatsChart } from '../services';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { SeatsSyncEvent, SeatsSyncPreviewResponse, SeatsSyncPreviewTicketType } from '../types';
import { useToastNotification } from 'hooks/useToastNotification';
import styles from './SyncPreviewDialog.module.css';

interface SyncPreviewDialogProps {
	event: SeatsSyncEvent;
	onClose: () => void;
	/** Opens the ReassignSeatsDialog for this event (rendered when the plan has orphaned seats). */
	onReassign?: () => void;
}

interface PreviewPlan {
	newTypes: SeatsSyncPreviewTicketType[];
	totalChanges: { name: string; from: number; to: number }[];
	keyChanges: { name: string; from: string | null; to: string | null }[];
}

const diffPlan = (data: SeatsSyncPreviewResponse): PreviewPlan => {
	const before = new Map((data.ticket_types_before ?? []).map((ticketType) => [ticketType.ticket_type_id, ticketType]));
	const plan: PreviewPlan = { newTypes: [], totalChanges: [], keyChanges: [] };
	for (const after of data.ticket_types ?? []) {
		const previous = before.get(after.ticket_type_id);
		if (!previous) {
			plan.newTypes.push(after);
			continue;
		}
		if (previous.total_amount !== after.total_amount) {
			plan.totalChanges.push({ name: after.name, from: previous.total_amount, to: after.total_amount });
		}
		if (previous.seats_category_key !== after.seats_category_key) {
			plan.keyChanges.push({ name: after.name, from: previous.seats_category_key, to: after.seats_category_key });
		}
	}
	return plan;
};

// transaction_type → operator-facing label for the reconciliation breakdown.
// An unlisted type falls back to its title-cased key so a new one still reads.
const TRANSACTION_LABELS: Record<string, string> = {
	primary_sold: 'Sold',
	card_box_office: 'Box office (card)',
	cash_box_office: 'Box office (cash)',
	comped: 'Comps',
	registration_mode: 'Registrations',
	transferred: 'Transfers in',
	switched_from: 'Switched in',
	switched_to: 'Switched out',
	refunded: 'Refunds',
	voided: 'Voided',
	comp_canceled: 'Comps canceled',
	sale_canceled: 'Sales canceled',
	transfer_canceled: 'Transfers canceled',
	external: 'External',
	external_canceled: 'External canceled',
};

const titleCase = (raw: string): string => raw.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const transactionBreakdown = (transactions?: Record<string, number> | null): string[] => {
	if (!transactions) return [];
	return Object.entries(transactions)
		.filter(([, count]) => count > 0)
		.sort((first, second) => second[1] - first[1])
		.map(([type, count]) => `${TRANSACTION_LABELS[type] ?? titleCase(type)} ${count}`);
};

const SeatChips: FC<{ seats: string[] }> = ({ seats }) => (
	<div className={styles.seatChips}>
		{seats.map((seat) => (
			<span key={seat} className={styles.seatChip}>
				{seat}
			</span>
		))}
	</div>
);

const Section: FC<{ heading: string; count?: number; children: ReactNode }> = ({ heading, count, children }) => (
	<div className={styles.section}>
		<h4 className={styles.sectionHeading}>
			{heading}
			{count !== undefined && <span className={styles.sectionCount}> ({count})</span>}
		</h4>
		{children}
	</div>
);

export const SyncPreviewDialog: FC<SyncPreviewDialogProps> = ({ event, onClose, onReassign }) => {
	const { t } = useTranslation();
	const { showNotification, TYPES } = useToastNotification();
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({
		queryKey: ['seats-sync-preview', event.event_id],
		queryFn: () => previewEventSeatsChart(event.event_id),
	});

	const apply = useMutation({
		mutationFn: () => syncEventSeatsChart(event.event_id),
		onSuccess: () => {
			showNotification({
				message: t('seatsSync-preview-applied', 'Event re-synced with its seating chart'),
				type: TYPES.SUCCESS,
			});
			queryClient.invalidateQueries({ queryKey: ['seats-sync-status'] });
			queryClient.invalidateQueries({ queryKey: ['seats-sync-preview', event.event_id] });
			onClose();
		},
		onError: (error) => {
			showNotification({
				message: t('seatsSync-preview-applyError', 'Failed to sync the event'),
				type: TYPES.ERROR,
				error,
			});
		},
	});

	const plan = data ? diffPlan(data) : null;
	const categoryLabel = (key: string | null): string => (key ? data?.category_labels?.[key] ?? `#${key}` : '—');
	const missing = data?.missing_seat_bookings ?? [];
	const held = data?.held_seats ?? [];
	const orphaned = data?.orphaned_seats ?? [];
	const retypes = data?.mistyped_tickets ?? data?.retyped_tickets ?? [];
	const doubleBooked = data?.double_booked_seats ?? [];
	const orphanedBookings = event.orphaned_bookings ?? [];
	const reconciliation = event.ticket_types ?? [];
	// Types flagged by the scan for a reason a chart re-sync can't fix: more live
	// tickets sold than seats booked on the chart (seatless comps/box-office).
	// This is the only drift that reaches the "nothing to change" branch, so name
	// it there instead of leaving the operator wondering why the row was flagged.
	const unresolvedTypes = reconciliation.filter(
		(ticketType) => ticketType.chart_booked != null && ticketType.sold > ticketType.chart_booked
	);
	// A duplicate the sync itself resolves — by re-keying a claimant off the
	// shared category — must not still read as "Apply won't fix these". Drop a
	// duplicate once the re-links leave it with one owner.
	const rekeyedNames = new Set((plan?.keyChanges ?? []).map((change) => change.name));
	const duplicates = (event.duplicate_keys ?? []).filter(
		(duplicate) => duplicate.ticket_types.filter((claimant) => !rekeyedNames.has(claimant.name)).length > 1
	);
	const hasSyncWork =
		!!plan &&
		(plan.newTypes.length > 0 ||
			plan.totalChanges.length > 0 ||
			plan.keyChanges.length > 0 ||
			missing.length > 0 ||
			retypes.length > 0);
	const needsOtherAction =
		orphaned.length > 0 || duplicates.length > 0 || doubleBooked.length > 0 || orphanedBookings.length > 0;

	const summaryParts = plan
		? [
				plan.newTypes.length > 0 && `${plan.newTypes.length} ${t('seatsSync-preview-sum-new', 'new type(s)')}`,
				plan.totalChanges.length > 0 &&
					`${plan.totalChanges.length} ${t('seatsSync-preview-sum-capacity', 'capacity fix(es)')}`,
				plan.keyChanges.length > 0 && `${plan.keyChanges.length} ${t('seatsSync-preview-sum-relink', 're-link(s)')}`,
				missing.length > 0 && `${missing.length} ${t('seatsSync-preview-sum-rebook', 'seat(s) to rebook')}`,
				retypes.length > 0 && `${retypes.length} ${t('seatsSync-preview-sum-retype', 'ticket(s) re-filed')}`,
		  ].filter(Boolean)
		: [];

	return (
		<PunchupDialogue maxWidth={640} onClose={onClose}>
			<DialogTitle>
				{t('seatsSync-preview-title', 'Review chart sync')} — {event.event_name} (#{event.event_id})
			</DialogTitle>

			{isLoading && <PunchupSpinner />}

			{!isLoading && plan && (
				<div className={styles.body}>
					{!hasSyncWork &&
						!needsOtherAction &&
						held.length === 0 &&
						(unresolvedTypes.length > 0 ? (
							<div className={styles.groupInfo}>
								<div className={styles.groupTitle}>{t('seatsSync-preview-whyFlagged', 'Why is this flagged?')}</div>
								<p className={styles.summary}>
									{t(
										'seatsSync-preview-whyFlaggedIntro',
										"A chart re-sync can't fix this — more tickets are sold than seats booked on the chart:"
									)}
								</p>
								<ul className={styles.list}>
									{unresolvedTypes.map((ticketType) => {
										const booked = ticketType.chart_booked ?? 0;
										const gap = ticketType.sold - booked;
										return (
											<li key={ticketType.ticket_type_id}>
												{ticketType.name}: {ticketType.sold} {t('seatsSync-sold', 'sold')} · {booked}{' '}
												{t('seatsSync-preview-bookedShort', 'booked')}
												{gap > 0 ? ` — ${gap} ${t('seatsSync-preview-noSeat', 'with no booked seat')}` : ''}
											</li>
										);
									})}
								</ul>
								<p className={styles.hint}>
									{t(
										'seatsSync-preview-whyFlaggedHint',
										'Likely seatless comps or box-office tickets. The sync only reconciles the chart — seat or void these from the box office.'
									)}
								</p>
							</div>
						) : (
							<p className={styles.clean}>
								{t('seatsSync-preview-clean', 'Nothing to change — this event matches its chart.')}
							</p>
						))}

					{hasSyncWork && (
						<div className={styles.groupApply}>
							<div className={styles.groupTitle}>{t('seatsSync-preview-groupApply', 'Will change when you Apply')}</div>
							{summaryParts.length > 0 && <p className={styles.summary}>{summaryParts.join(' · ')}</p>}

							{plan.newTypes.length > 0 && (
								<Section
									heading={t('seatsSync-preview-newTypes', 'Ticket types to be created')}
									count={plan.newTypes.length}
								>
									<ul className={styles.list}>
										{plan.newTypes.map((ticketType) => (
											<li key={ticketType.ticket_type_id}>
												{ticketType.name} — {ticketType.total_amount} {t('seatsSync-preview-seats', 'seats')}
											</li>
										))}
									</ul>
									<p className={styles.hint}>
										{t('seatsSync-preview-newTypesHint', 'Created at $0 — set the price right after applying.')}
									</p>
								</Section>
							)}

							{plan.totalChanges.length > 0 && (
								<Section
									heading={t('seatsSync-preview-totals', 'Capacity corrections')}
									count={plan.totalChanges.length}
								>
									<ul className={styles.list}>
										{plan.totalChanges.map((change) => (
											<li key={change.name}>
												{change.name}: {change.from} → {change.to}
											</li>
										))}
									</ul>
								</Section>
							)}

							{plan.keyChanges.length > 0 && (
								<Section heading={t('seatsSync-preview-keys', 'Category re-links')} count={plan.keyChanges.length}>
									<ul className={styles.list}>
										{plan.keyChanges.map((change) => (
											<li key={change.name}>
												{change.name}: {categoryLabel(change.from)} → {categoryLabel(change.to)}
											</li>
										))}
									</ul>
								</Section>
							)}

							{missing.length > 0 && (
								<Section
									heading={t('seatsSync-preview-missing', 'Sold seats to be rebooked on the chart')}
									count={missing.length}
								>
									<SeatChips seats={missing} />
								</Section>
							)}

							{retypes.length > 0 && (
								<Section
									heading={t('seatsSync-preview-retypes', "Tickets re-filed under their seat's ticket type")}
									count={retypes.length}
								>
									<ul className={`${styles.list} ${styles.scrollList}`}>
										{retypes.map((retype) => (
											<li key={retype.ticket_id}>
												{retype.seat_label}: {retype.from_ticket_type_name} → {retype.to_ticket_type_name}
											</li>
										))}
									</ul>
									<p className={styles.hint}>
										{t(
											'seatsSync-preview-retypesHint',
											'Buyers keep their seats and prices — only the ticket type attribution moves.'
										)}
									</p>
								</Section>
							)}
						</div>
					)}

					{needsOtherAction && (
						<div className={styles.groupDanger}>
							<div className={styles.groupTitle}>
								{t('seatsSync-preview-groupDanger', "Apply won't fix these — act separately")}
							</div>

							{doubleBooked.length > 0 && (
								<Section heading={t('seatsSync-preview-doubleBooked', 'Double-sold seats')} count={doubleBooked.length}>
									<ul className={`${styles.list} ${styles.scrollList}`}>
										{doubleBooked.map((seat) => (
											<li key={seat.seat_label}>
												{seat.seat_label} — {t('seatsSync-preview-doubleBookedTickets', 'tickets')}{' '}
												{seat.ticket_ids.join(' + ')}
											</li>
										))}
									</ul>
									<p className={styles.hint}>
										{t(
											'seatsSync-preview-doubleBookedHint',
											'Two active tickets own this seat — refund or reseat one of them manually.'
										)}
									</p>
								</Section>
							)}

							{orphanedBookings.length > 0 && (
								<Section
									heading={t('seatsSync-preview-orphanedBookings', 'Chart seats booked with no ticket')}
									count={orphanedBookings.length}
								>
									<ul className={`${styles.list} ${styles.scrollList}`}>
										{orphanedBookings.map((booking) => (
											<li key={`${booking.seats_category_key}-${booking.seat_label}`}>
												{booking.seat_label} — {booking.category_label || `#${booking.seats_category_key}`}
											</li>
										))}
									</ul>
									<p className={styles.hint}>
										{t(
											'seatsSync-preview-orphanedBookingsHint',
											'Booked on the chart but no live ticket owns them — a purchase, refund or switch failed to release the seat. Free them on the chart.'
										)}
									</p>
								</Section>
							)}

							{orphaned.length > 0 && (
								<Section
									heading={t('seatsSync-preview-orphans', 'Sold seats no longer on the chart')}
									count={orphaned.length}
								>
									<SeatChips seats={orphaned} />
									{onReassign && (
										<PunchupButton variant="outline" onClick={onReassign}>
											{t('seatsSync-preview-reassignButton', 'Reassign these seats…')}
										</PunchupButton>
									)}
								</Section>
							)}

							{duplicates.length > 0 && (
								<Section
									heading={t('seatsSync-preview-duplicate', 'Category claimed by multiple ticket types')}
									count={duplicates.length}
								>
									{duplicates.map((duplicate) => {
										const owner = duplicate.ticket_types.find((claimant) => claimant.matches_chart);
										const label = duplicate.category_label || `#${duplicate.seats_category_key}`;
										return (
											<div key={duplicate.seats_category_key} className={styles.duplicate}>
												<div className={styles.duplicateHead}>
													{label}
													{duplicate.chart_capacity !== null &&
														` — ${duplicate.chart_capacity} ${t('seatsSync-preview-seats', 'seats')}`}
												</div>
												<ul className={styles.list}>
													{duplicate.ticket_types.map((claimant) => (
														<li key={claimant.name}>
															{claimant.name} ({claimant.total_amount})
															{claimant.matches_chart
																? ` — ${t('seatsSync-preview-dupMatch', 'matches this category')}`
																: ''}
														</li>
													))}
												</ul>
												<p className={styles.hint}>
													{owner
														? `${owner.name} ${t(
																'seatsSync-preview-dupOwner',
																'matches this category by seat count — point it here and re-point the other type.'
														  )}`
														: t(
																'seatsSync-preview-duplicateFix',
																"Only one type can sell a category — clear the extra type's category manually."
														  )}
												</p>
											</div>
										);
									})}
								</Section>
							)}
						</div>
					)}

					{reconciliation.length > 0 && (
						<div className={styles.groupInfo}>
							<div className={styles.groupTitle}>
								{t('seatsSync-preview-groupReconcile', 'Category reconciliation')}
							</div>
							<div className={styles.reconcileList}>
								{reconciliation.map((ticketType) => {
									const breakdown = transactionBreakdown(ticketType.transactions);
									return (
										<div key={ticketType.ticket_type_id} className={styles.reconcileCard}>
											<div className={styles.reconcileHead}>
												{ticketType.name} · {categoryLabel(ticketType.seats_category_key)}
											</div>
											<div className={styles.reconcileGrid}>
												<span className={styles.reconcileLabel}>{t('seatsSync-preview-chart', 'Chart')}</span>
												<span>
													{ticketType.chart_for_sale ?? '—'} {t('seatsSync-preview-forSale', 'for sale')} ·{' '}
													{ticketType.chart_not_for_sale ?? '—'} {t('seatsSync-preview-notForSale', 'not for sale')} ·{' '}
													{ticketType.chart_held ?? '—'} {t('seatsSync-preview-heldShort', 'held')} ·{' '}
													{ticketType.chart_booked ?? '—'} {t('seatsSync-preview-bookedShort', 'booked')}
													{ticketType.chart_capacity !== null &&
														` (${ticketType.chart_capacity} ${t('seatsSync-preview-capacityShort', 'capacity')})`}
												</span>
												<span className={styles.reconcileLabel}>{t('seatsSync-preview-db', 'DB')}</span>
												<span>
													{ticketType.total_amount} {t('seatsSync-preview-total', 'total')} · {ticketType.sold}{' '}
													{t('seatsSync-sold', 'sold')}
												</span>
											</div>
											{breakdown.length > 0 && <div className={styles.reconcileTx}>{breakdown.join(' · ')}</div>}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{held.length > 0 && (
						<div className={styles.groupInfo}>
							<div className={styles.groupTitle}>{t('seatsSync-preview-groupInfo', 'Info only')}</div>
							<Section heading={t('seatsSync-preview-held', "Sold seats held in a shopper's cart")} count={held.length}>
								<SeatChips seats={held} />
								<p className={styles.hint}>
									{t('seatsSync-preview-heldHint', 'Not touched by the sync — retry after the holds expire.')}
								</p>
							</Section>
						</div>
					)}

					<ButtonGroup>
						<PunchupButton variant="outline" onClick={onClose} disabled={apply.isLoading}>
							{t('seatsSync-preview-cancel', 'Cancel')}
						</PunchupButton>
						<PunchupButton
							disabled={!hasSyncWork || apply.isLoading}
							isLoading={apply.isLoading}
							onClick={() => apply.mutate()}
						>
							{t('seatsSync-preview-apply', 'Apply sync')}
						</PunchupButton>
					</ButtonGroup>
				</div>
			)}
		</PunchupDialogue>
	);
};
